import {Descendant, Text} from "slate"
import escapeHtml from "escape-html"
import {Communication} from "types/communication"
import moment from "moment"
import {Auth} from "types/auth"
import {getFullAddress, getFullName} from "./user"
import {formatCurrency, formatDecimal, getBaseUrl} from "./common"
import {FieldLabelElement} from "components/RichTextEditor/slate-custom-types"
import {TemplateUserDetails} from "types/templates"

const LINK_PROTOCOLS = ["http:", "https:", "mailto:"]
const IMAGE_PROTOCOLS = ["http:", "https:"]
const PAYMENT_LINK_STYLE = "color: #1a73e8; font-weight: bold;"

type FieldLabelLink = {kind: "link"; href: string; text: string; style?: string}

const isFieldLabelLink = (value: unknown): value is FieldLabelLink =>
    typeof value === "object" && value !== null && (value as FieldLabelLink).kind === "link"

/** HTML-escapes a resolved value for element content or a double-quoted attribute. */
const toHtmlText = (value: unknown): string => escapeHtml(value == null ? "" : String(value))

/**
 * Returns the URL when the browser would resolve it to one of `allowedProtocols` (scheme-less and relative
 * URLs cannot carry a script scheme and are kept), otherwise null. Parsing with URL matches how the browser
 * reads the attribute, including stripped tabs/newlines such as `java\tscript:`.
 */
export const toSafeUrl = (url: unknown, allowedProtocols: string[] = LINK_PROTOCOLS): string | null => {
    if (typeof url !== "string" || !url.trim()) {
        return null
    }
    try {
        const {protocol} = new URL(url, "https://relative.invalid/")
        return allowedProtocols.includes(protocol) ? url : null
    } catch {
        return null
    }
}

const buildLinkHtml = ({href, text, style}: Omit<FieldLabelLink, "kind">): string => {
    const safeHref = toSafeUrl(href, LINK_PROTOCOLS)
    if (safeHref === null) {
        return toHtmlText(text)
    }
    const styleAttr = style ? ` style="${toHtmlText(style)}"` : ""
    return `<a target="_blank" rel="noopener noreferrer"${styleAttr} href="${toHtmlText(safeHref)}">${toHtmlText(
        text
    )}</a>`
}

export const serializeTemplate = (
    node: Descendant,
    userData: TemplateUserDetails,
    options: {
        selectedTemplate
        serializeWithoutUser
        userDateFormat
        userTimeFormat
    }
) => {
    const {
        userDetails,
        profile,
        campusDetails,
        admissionAdvisor,
        finAidAdvisor,
        academicAdvisor,
        studentLedgerStats,
        studentOverallStats,
        paymentPlanItemIds
    } = userData
    const {selectedTemplate, serializeWithoutUser, userDateFormat, userTimeFormat} = options
    if (Text.isText(node)) {
        if (selectedTemplate?.editorType === Communication.EditorType.Html) {
            return node.text
        }

        let string = escapeHtml(node.text)
        if (node.bold) {
            string = `<strong>${string}</strong>`
        }
        if (node.italic) {
            string = `<i>${string}</i>`
        }
        if (node.underline) {
            string = `<u>${string}</u>`
        }
        if (node.code) {
            string = `<code>${string}</code>`
        }
        return string
    }

    const children = node.children.map((n) => serializeTemplate(n, userData, options)).join("")

    let attrs = ""
    if (node.align) {
        attrs += ` align="${toHtmlText(node.align)}"`
    }

    if (node.type === "block-quote") {
        return `<blockquote${attrs}><p>${children}</p></blockquote>`
    } else if (node.type === "paragraph") {
        if (selectedTemplate?.editorType === Communication.EditorType.Html) {
            return children
        }
        return `<p${attrs}>${children}</p>`
    } else if (node.type === "link") {
        const href = toSafeUrl(node.url, LINK_PROTOCOLS)
        if (href === null) {
            return children
        }
        return `<a href="${toHtmlText(href)}" target="_blank" rel="noopener noreferrer"${attrs}>${children}</a>`
    } else if (node.type === "heading-one") {
        return `<h1${attrs}>${children}</h1>`
    } else if (node.type === "heading-two") {
        return `<h2${attrs}>${children}</h2>`
    } else if (node.type === "heading-three") {
        return `<h3${attrs}>${children}</h3>`
    } else if (node.type === "heading-four") {
        return `<h4${attrs}>${children}</h4>`
    } else if (node.type === "heading-five") {
        return `<h5${attrs}>${children}</h5>`
    } else if (node.type === "heading-six") {
        return `<h6${attrs}>${children}</h6>`
    } else if (node.type === "image") {
        const src = toSafeUrl(node.url, IMAGE_PROTOCOLS)
        return src === null ? "" : `<img${attrs} src="${toHtmlText(src)}" />`
    } else if (node.type === "numbered-list") {
        return `<ol${attrs}>${children}</ol>`
    } else if (node.type === "bulleted-list") {
        return `<ul${attrs}>${children}</ul>`
    } else if (node.type === "list-item") {
        return `<li${attrs}>${children}</li>`
    } else if (node.type === "field-label") {
        return populateFieldLabelHtml(node as FieldLabelElement, userData, options)
    } else if (node.type === "field-input") {
        switch (node.input) {
            case "text":
                return toHtmlText(node.value)
            case "date":
                return toHtmlText(moment(node.value).format(userDateFormat))
            case "time":
                return toHtmlText(moment(node.value).format(userTimeFormat))
        }
    } else if (node.type === "field-label-input") {
        if (studentOverallStats?.lastDayOfAttendance && node.field === "lastDayOfAttendancePlus") {
            const lastDayOfAttendance = studentOverallStats.lastDayOfAttendance
                ? moment(studentOverallStats.lastDayOfAttendance).add(Number(node.value), "days").format(userDateFormat)
                : "-"
            return toHtmlText(lastDayOfAttendance)
        }
    } else if (node.type === "embed-video") {
        const src = toSafeUrl(node.videoId, IMAGE_PROTOCOLS)
        return src === null ? "" : `<iframe src="${toHtmlText(src)}" allowFullScreen></iframe>`
    } else if (node.type === "mention") {
        return `[@${node.character}:${node.userId}]`
    } else {
        return children
    }
}

type FieldLabelOptions = {
    serializeWithoutUser?: boolean
    userDateFormat?: string
    userTimeFormat?: string
}

/**
 * Raw value of a data field, for plain-text consumers such as the rich-text editor display
 * (`replaceFieldValues`). Link fields come back as an `<a>` string. Never insert the result into HTML
 * yourself; use `populateFieldLabelHtml`.
 */
export const populateFieldLabelValue = (
    node: FieldLabelElement,
    userData: TemplateUserDetails,
    options?: FieldLabelOptions
): string => {
    const value = resolveFieldLabel(node, userData, options)
    return isFieldLabelLink(value) ? buildLinkHtml(value) : value
}

/**
 * HTML-safe value of a data field for chat email/SMS bodies: resolved student/staff data is escaped and
 * link fields are built with an escaped, protocol-checked href. The `[fieldKey]` token used when sending
 * to several recipients (`serializeWithoutUser`) is returned unchanged for the backend to resolve.
 */
export const populateFieldLabelHtml = (
    node: FieldLabelElement,
    userData: TemplateUserDetails,
    options?: FieldLabelOptions
): string => {
    if (options?.serializeWithoutUser) {
        return `[${node.field}]`
    }
    const value = resolveFieldLabel(node, userData, options)
    return isFieldLabelLink(value) ? buildLinkHtml(value) : toHtmlText(value)
}

const resolveFieldLabel = (
    node: FieldLabelElement,
    userData: TemplateUserDetails,
    options?: FieldLabelOptions
): any => {
    const {serializeWithoutUser, userDateFormat, userTimeFormat} = options ?? {}
    const {
        userDetails,
        profile,
        campusDetails,
        admissionAdvisor,
        finAidAdvisor,
        academicAdvisor,
        studentLedgerStats,
        studentOverallStats,
        paymentPlanItemIds = []
    } = userData

    const defaultShift = (profile?.shifts || []).find((shift) => !!shift.isActive && shift.type === "default")
    const nonDefaultShift = (profile?.shifts || []).find((shift) => !!shift.isActive)

    const defaultPhase = (profile?.phases || []).find((phase) => !!phase.isActive && phase.type === "default")
    const nonDefaultPhase = (profile?.phases || []).find((phase) => !!phase.isActive)

    if (serializeWithoutUser) {
        return `[${node.field}]`
    }

    if (
        userDetails &&
        userDetails[node.field] &&
        [
            "firstName",
            "middleName",
            "lastName",
            "gender",
            "ssn",
            "address",
            "city",
            "state",
            "postalCode",
            "country",
            "customUserId",
            "nickNamePreferedName"
        ].indexOf(node.field) >= 0
    ) {
        return userDetails[node.field]
    } else if (userDetails?.birthDate && node.field === "birthDate") {
        return moment(userDetails.birthDate).format(userDateFormat)
    } else if (node.field === "fullName") {
        return getFullName(userDetails)
    } else if (userDetails?.emails && node.field === "email") {
        return userDetails.emails?.filter((email) => !!email.isPrimary)?.map((email) => email.email)
    } else if (userDetails?.phones && node.field === "phone") {
        return userDetails.phones?.filter((phone) => !!phone.isPrimary)?.map((phone) => phone.phone)
    } else if (profile?.campus && node.field === "campuses") {
        return profile.campus
    } else if (campusDetails?.email && node.field === "campusEmail") {
        return campusDetails.email
    } else if (campusDetails?.phone && node.field === "campusPhone") {
        return campusDetails.phone
    } else if (campusDetails?.officialCampusName && node.field === "campusOfficialName") {
        return campusDetails.officialCampusName
    } else if (campusDetails?.schoolCodes?.length && node.field === "campusFafsaCodes") {
        return `${campusDetails.schoolCodes.map((schoolCode) => schoolCode.code).join(", ") ?? ""}`
    } else if (campusDetails && node.field === "campusAddress") {
        return getFullAddress(campusDetails)
    } else if (campusDetails !== null && node.field === "campusCity") {
        return campusDetails.city ?? ""
    } else if (campusDetails !== null && node.field === "campusState") {
        return campusDetails.state ?? ""
    } else if (campusDetails !== null && node.field === "campusPostalCode") {
        return campusDetails.postalCode ?? ""
    } else if (campusDetails !== null && node.field === "campusWebsiteUrl") {
        return campusDetails.websiteUrl ?? campusDetails.website ?? ""
    } else if (profile?.currentMajor?.name && node.field === "program") {
        return profile.currentMajor.name
    } else if (profile?.customProfileId !== null && node.field === "customProfileId") {
        return profile.customProfileId
    } else if (node.field === "enrollmentStatus") {
        return profile?.enrollmentStatus ?? userDetails?.enrollmentStatus ?? ""
    } else if (node.field === "efc" && profile?.efc !== null && profile.efc !== "") {
        return profile.efc
    } else if (node.field === "dependencyStatus" && profile?.dependencyStatus !== null) {
        return String(profile.dependencyStatus)
    } else if (node.field === "awardYear") {
        return profile?.awardYear ?? userDetails?.awardYear ?? ""
    } else if (node.field === "admissionStatus") {
        return userData?.admissionStatus ?? ""
    } else if (node.field === "finAidStatus") {
        return userData?.finAidStatus ?? ""
    } else if (node.field === "ISIR") {
        return userData?.isir || ""
    } else if (profile?.startDate && node.field === "startDate") {
        return moment(profile.startDate).format(userDateFormat)
    } else if (profile?.projectedEndDate && node.field === "projectedEndDate") {
        return moment(profile.projectedEndDate).format(userDateFormat)
    } else if (profile?.naccasGradDate && node.field === "naccasGradDate") {
        return moment(profile.naccasGradDate).format(userDateFormat)
    } else if (profile?.contractEndDate && node.field === "contractGraduationDate") {
        return moment(profile.contractEndDate).format(userDateFormat)
    } else if (profile?.orientationDate && node.field === "orientationDate") {
        return moment(profile.orientationDate).format(userDateFormat)
    } else if (profile?.temporaryOutReturnDate && node.field === "loaReturnDate") {
        return moment(profile.temporaryOutReturnDate).format(userDateFormat)
    } else if (profile?.currentTerm?.name && node.field === "term") {
        return profile.currentTerm.name
    } else if (profile?.startingTerm?.name && node.field === "startTerm") {
        return profile.startingTerm.name
    } else if (userDetails?.collegeDetails[0]?.graduationDate && node.field === "graduationDate") {
        return moment(userDetails.collegeDetails[0].graduationDate).format(userDateFormat)
    } else if (node.field === "documentsFolder") {
        const link: FieldLabelLink = {
            kind: "link",
            href: `${getBaseUrl()}/users/${encodeURIComponent(userDetails?.id ?? "")}?tab=${
                Auth.UserDetailTab.Documents
            }`,
            text: "Documents Folder"
        }
        return link
    } else if (admissionAdvisor && node.field === "admissionAdvisor") {
        return `${getFullName(admissionAdvisor)}`
    } else if (admissionAdvisor && node.field === "admissionAdvisorEmail") {
        return admissionAdvisor.advisorEmail
    } else if (admissionAdvisor && node.field === "admissionAdvisorPhone") {
        return admissionAdvisor.advisorPhone
    } else if (studentOverallStats?.lastDayOfAttendance && node.field === "lastDayOfAttendance") {
        return studentOverallStats.lastDayOfAttendance
            ? moment(studentOverallStats.lastDayOfAttendance).format(userDateFormat)
            : "-"
    } else if (
        studentOverallStats?.averageAttendancePercentage !== null &&
        (node.field === "attendance" || node.field === "averageAttendancePercentage")
    ) {
        return studentOverallStats.averageAttendancePercentage
            ? `${studentOverallStats.averageAttendancePercentage}`
            : "-"
    } else if (node.field === "cgpa") {
        const cgpaVal = studentOverallStats?.cgpa
        return cgpaVal !== null && cgpaVal !== ""
            ? formatDecimal(typeof cgpaVal === "number" ? cgpaVal : Number(cgpaVal))
            : "-"
    } else if (studentOverallStats?.missedAttendanceHours != null && node.field === "missedAttendanceHours") {
        return studentOverallStats.missedAttendanceHours != null ? `${studentOverallStats.missedAttendanceHours}` : "-"
    } else if (studentOverallStats?.missedAttendanceDays != null && node.field === "missedAttendanceDays") {
        return studentOverallStats.missedAttendanceDays != null ? `${studentOverallStats.missedAttendanceDays}` : "-"
    } else if (studentOverallStats?.totalTransferHours != null && node.field === "transferHours") {
        return studentOverallStats.totalTransferHours != null ? `${studentOverallStats.totalTransferHours}` : "-"
    } else if (studentOverallStats?.totalHoursAttempted != null && node.field === "attemptedHours") {
        return studentOverallStats.totalHoursAttempted != null ? `${studentOverallStats.totalHoursAttempted}` : "-"
    } else if (studentOverallStats?.totalHoursCompleted != null && node.field === "completedHours") {
        return studentOverallStats.totalHoursCompleted != null ? `${studentOverallStats.totalHoursCompleted}` : "-"
    } else if (studentOverallStats?.totalHoursRemaining != null && node.field === "remainingHours") {
        return studentOverallStats.totalHoursRemaining != null ? `${studentOverallStats.totalHoursRemaining}` : "-"
    } else if (studentOverallStats?.totalDistEdHours != null && node.field === "distantEduHours") {
        return studentOverallStats.totalDistEdHours != null ? `${studentOverallStats.totalDistEdHours}` : "-"
    } else if (studentOverallStats?.hoursScheduled != null && node.field === "scheduledHours") {
        return studentOverallStats.hoursScheduled != null ? `${studentOverallStats.hoursScheduled}` : "-"
    } else if (finAidAdvisor && node.field === "finAidAdvisor") {
        return `${getFullName(finAidAdvisor)}`
    } else if (finAidAdvisor && node.field === "finAidAdvisorEmail") {
        return finAidAdvisor.advisorEmail
    } else if (finAidAdvisor && node.field === "finAidAdvisorPhone") {
        return finAidAdvisor.advisorPhone
    } else if (studentLedgerStats?.nextPaymentAmount != null && node.field === "nextPaymentAmount") {
        return studentLedgerStats.nextPaymentAmount != null ? formatCurrency(studentLedgerStats.nextPaymentAmount) : "-"
    } else if (studentLedgerStats?.nextPaymentDate != null && node.field === "nextPaymentDueDate") {
        return studentLedgerStats.nextPaymentDate != null
            ? moment(studentLedgerStats.nextPaymentDate).format(userDateFormat)
            : "-"
    } else if (studentLedgerStats?.paymentPastDueAmount != null && node.field === "pastDueAmount") {
        return studentLedgerStats.paymentPastDueAmount != null
            ? formatCurrency(studentLedgerStats.paymentPastDueAmount)
            : "-"
    } else if (node.field === "accountBalance") {
        return typeof userData.accountBalance === "number" ? formatCurrency(userData.accountBalance) : "-"
    } else if (node.field === "paymentPlanBalance") {
        return typeof userData.paymentPlanBalance === "number" ? formatCurrency(userData.paymentPlanBalance) : "-"
    } else if (academicAdvisor && node.field === "academicAdvisor") {
        return `${getFullName(academicAdvisor)}`
    } else if (academicAdvisor && node.field === "academicAdvisorEmail") {
        return academicAdvisor.advisorEmail
    } else if (academicAdvisor && node.field === "academicAdvisorPhone") {
        return academicAdvisor.advisorPhone
    } else if ((defaultShift || nonDefaultShift) && node.field === "academicShift") {
        return (defaultShift ?? nonDefaultShift).name
    } else if ((defaultPhase || nonDefaultPhase) && node.field === "academicPhase") {
        return (defaultPhase ?? nonDefaultPhase).name
    } else if (profile?.academicTrack?.name && node.field === "academicTrack") {
        return profile.academicTrack.name
    } else if (node.field === "todaysDate") {
        return moment().format(userDateFormat)
    } else if (node.field === "today_month") {
        return moment().format("MMMM")
    } else if (node.field === "today_year") {
        return moment().format("YYYY")
    } else if (node.field === "paymentLinkGeneral" || node.field === "paymentLinkPlanItem") {
        let href = `${getBaseUrl()}/make-payment?studentId=${encodeURIComponent(profile?.id ?? "")}`
        if (node.field === "paymentLinkPlanItem") {
            href += `&planItemIds=${paymentPlanItemIds.map((id) => encodeURIComponent(id)).join(",")}`
        }
        const link: FieldLabelLink = {kind: "link", href, text: "Make Payment", style: PAYMENT_LINK_STYLE}
        return link
    }
    return userData[node.field] ?? ""
}
